import { useTranslation } from 'react-i18next'

import LinkText from '@/components/LinkText/LinkText'
import Table, { type Column } from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import NotApplicable from '@/components/common/NotApplicable/NotApplicable'
import { useNumberColumn } from '@/hooks/useColumns'
import { PRIORITY } from '@/hooks/useStickyElement'
import useStickyStore from '@/store/useStickyStore'

import styles from './BasketSummaryTable.module.scss'
import CurriculumTimeUnitTag from '../components/CurriculumTimeUnitTag'
import type { CurriculumTimeUnit } from '../constants/curriculum-time-unit'
import type { BasketSummaryTableRow } from '../types/basket-summary-table-row'

type BasketSummaryTableProps = {
  id: string
  title: string
  data: BasketSummaryTableRow[]
  shouldLinkToCourse: boolean
}

const highlightedRows = [1, 2, 5, 6]
const hoursPerYearCourseDisplayableRows = [1, 3, 5, 7]

const BasketSummaryTable = (props: BasketSummaryTableProps) => {
  const { t } = useTranslation('curriculumsManagement')

  const { elements } = useStickyStore()

  const pageHeaderHeight =
    elements.find(element => element.priority === PRIORITY.navbar)?.height || 0
  const tableTopBarHeight =
    elements.find(element => element.priority === PRIORITY.tableTopBar)
      ?.height || 0

  const columns: Column<BasketSummaryTableRow>[] = [
    useNumberColumn(),
    {
      key: 'course',
      title: t('header.course'),
      width: TABLE_CELL_WIDTH.LG,

      render: (_: unknown, row: BasketSummaryTableRow) => {
        if (!row.course) return <NotApplicable />

        return props.shouldLinkToCourse ? (
          <LinkText
            to="/courses-management/$courseId"
            params={{
              courseId: row.course.id
            }}
          >
            {row.course.name}
          </LinkText>
        ) : (
          row.course.name
        )
      }
    },
    {
      key: 'timeUnit',
      dataIndex: 'timeUnit',
      title: t('header.time-unit'),
      width: TABLE_CELL_WIDTH.LG,
      render: (timeUnit: CurriculumTimeUnit) => (
        <CurriculumTimeUnitTag timeUnit={timeUnit} />
      )
    },
    ...Array.from({ length: 8 }, (_, i) => ({
      key: `sem${i + 1}`,
      title: t(`header.semester-no`, { count: i + 1 }),
      width: TABLE_CELL_WIDTH.SM,
      render: (__: unknown, row: BasketSummaryTableRow) =>
        row.semesters[i] || <NotApplicable />,
      isHighlighted: highlightedRows.includes(i + 1),
      onCell: (row: BasketSummaryTableRow) => {
        const isYearlyCourse = row.timeUnit === 'year'
        const isDisplayableSemester =
          hoursPerYearCourseDisplayableRows.includes(i + 1)

        if (isYearlyCourse && isDisplayableSemester) return { colSpan: 2 }
        if (isYearlyCourse && !isDisplayableSemester) return { colSpan: 0 }
        return { colSpan: 1 }
      }
    })),
    {
      key: 'total',
      title: t('header.total'),
      dataIndex: 'total',
      width: TABLE_CELL_WIDTH.SM,
      isHighlighted: true,
      render: (_: unknown, row: BasketSummaryTableRow) => (
        <span className={styles.total}>{row.total}</span>
      )
    }
  ]

  return (
    <div className={styles.section}>
      <h2 className={styles.header}>{props.title}</h2>
      <Table
        id={`curriculum-summary-table-${props.id}`}
        columns={columns}
        data={props.data}
        count={props.data.length}
        headerTop={pageHeaderHeight + tableTopBarHeight}
        topBarTop={pageHeaderHeight}
        caption={t('header.curriculum-summary')}
      />
    </div>
  )
}

export default BasketSummaryTable
